import React from 'react';
import WalletIconWithBadge from '../wallet/WalletIconWithBadge';
import { Divider, Box, Tooltip } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { DeleteOutlined } from '@mui/icons-material';
import ArrowOutwardOutlinedIcon from '@mui/icons-material/ArrowOutwardOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import { useSelector } from 'react-redux';
import CopyButton from '../common/CopyButton';
import TransactionCommonModal from '../common/TransactionCommonModal';
import { fiatCurrencyList, truncateMiddle } from '../../helpers/constant';
import clsx from 'clsx';
import useWindowWidth from '../../hooks/useWindowWidth';

const SavedAccountAddress = ({
    onClose,
    data = [],
    open,
    onEdit,
    onDeleteAccount,
    handlePayout,
}) => {
    const currencyIcon = fiatCurrencyList.find(
        x => x.currencyShortName === data.currency,
    )?.currencyIcon;

    const clientData = useSelector(state => state.juno.clientData);
    const { readOnly } = clientData || {};
    const windowWidth = useWindowWidth();

    const fieldConfigs = [
        {
            label: 'Bank name',
            key: 'bankName',
            copyValue: data?.bankName,
        },
        {
            label: 'Beneficiary Address',
            key: 'beneficiaryAddress',
            copyValue: data?.beneficiaryAddress,
        },
        {
            label: 'Bank Address',
            key: 'bankAddress',
            copyValue: data?.bankAddress,
        },
        {
            label: 'Bank Country',
            key: 'bankCountry',
            copyValue: data?.bankCountry,
        },
        {
            label: 'Beneficiary Name',
            key: 'beneficiaryName',
            copyValue: data?.beneficiaryName,
        },
        {
            label: 'Beneficiary Account number',
            key: 'beneficiaryAccountNumber',
            copyValue: data?.beneficiaryAccountNumber,
        },
        {
            label: 'Beneficiary Country',
            key: 'beneficiaryCountry',
            copyValue: data?.beneficiaryCountry,
        },
        {
            label: 'IBAN',
            key: 'iban',
            copyValue: data?.iban,
        },
        {
            label: 'Sort code',
            key: 'sortCode',
            copyValue: data?.sortCode ? 'Corporation Bank' : null,
        },
        {
            label: 'SWIFT',
            key: 'swift',
            copyValue: data?.swift,
        },
        {
            label: 'Additional bank info (Routing Number)',
            key: 'additionalBankDetails',
            copyValue: data?.additionalBankDetails,
        },
    ];

    const handleEditClick = () => {
        if (onEdit) {
            onClose();
            onEdit(data);
        }
    };

    const handleDeleteClick = () => {
        if (onDeleteAccount) {
            onDeleteAccount(data);
        }
    };

    return (
        <TransactionCommonModal
            open={open}
            onClose={onClose}
            childrenCss={{ mb: 0 }}
            heading={
                <div className="flex items-center !gap-5">
                    <WalletIconWithBadge
                        currencyIcon={currencyIcon}
                        height={10}
                        width={10}
                        iconDimension={23}
                        imageDimension={4}
                        className="!bg-card-bg"
                    />
                    <div className="flex flex-col gap-1">
                        <p className="text-[#18181B] dark:text-[#FFF] text-[20px] font-semibold leading-8 tracking-[0.2px] uppercase">
                            {truncateMiddle(data?.accountNickName ,windowWidth >= 640 ? 30 : 10)}
                        </p>
                        <p className="text-[#51525C] dark:text-[#D1D1D6] text-[14px] font-medium leading-5 uppercase">
                           {truncateMiddle(data?.beneficiaryName ,windowWidth >= 640 ? 30 : 10)}
                        </p>
                    </div>
                </div>
            }
            showBackButton={false}
            showCloseIcon={true}>
            <div className="flex flex-col items-start bg-[#FFF] dark:!bg-[#26272B] w-full relative gap-4">
                {/* Content - Scrollable */}
                <Box
                    sx={{
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        flex: 1,
                        width: '100%',
                        '&::-webkit-scrollbar': { display: 'none' },
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                        padding: 0,
                    }}>
                    <div className="w-full h-[600px] overflow-y-scroll custom-scrollbar">
                        {fieldConfigs.map((field, index) => (
                            <React.Fragment key={field.key}>
                                <div className="flex justify-between items-center">
                                    <div>
                                        <p className="text-[#51525C] dark:!text-[#D1D1D6] text-xs font-medium leading-4 uppercase">
                                            {field.label}
                                        </p>
                                        <p className="text-[#18181B] dark:text-[#FFF] text-sm font-medium leading-5 uppercase">
                                         {truncateMiddle(data?.[field.key] || '-' ,windowWidth >= 640 ? 40 : 20)}
                                        </p>
                                    </div>
                                    {field.copyValue && (
                                        <CopyButton
                                            className="text-[#1A8A7E]"
                                            copyValue={field.copyValue}
                                        />
                                    )}
                                </div>
                                <Divider className="bg-input-border my-2" />
                            </React.Fragment>
                        ))}
                    </div>
                </Box>

                {/* Buttons - Fixed */}
                <div className="flex justify-between items-center w-full !p-0 bg-[#FFF] dark:!bg-[#26272B] pt-20">
                    <div className="flex items-center gap-2">
                        <Tooltip arrow placement="top" title={'EDIT'}>
                            <div
                                className={clsx("flex w-11 p-[10px] justify-center items-center gap-1 rounded-lg bg-[#E4E4E7] cursor-pointer hover:bg-[#D4D4D8] dark:bg-[#3F3F46] dark:hover:bg-[#4F4F56]", readOnly && "cursor-not-allowed opacity-50")}
                                onClick={readOnly ? null : handleEditClick}>
                                <EditOutlinedIcon className="text-[#18181B] dark:text-[#FFF]" />
                            </div>
                        </Tooltip>
                        <Tooltip arrow placement="top" title={'DELETE'}>
                            <div
                                className={clsx("flex w-11 p-[10px] justify-center items-center gap-1 rounded-lg bg-[#E4E4E7] cursor-pointer hover:bg-[#D4D4D8] dark:bg-[#3F3F46] dark:hover:bg-[#4F4F56]", readOnly && "cursor-not-allowed opacity-50")}
                                onClick={readOnly ? null : handleDeleteClick}>
                                <DeleteOutlined className="text-[#18181B] dark:text-[#FFF]" />
                            </div>
                        </Tooltip>
                    </div>

                    <div className="flex items-center gap-2">
                        <Tooltip arrow placement="top" title={'WITHDRAWAL'}>
                            <div
                                className={clsx("flex w-11 p-[10px] justify-center items-center gap-1 rounded-lg bg-[#E4E4E7] cursor-pointer hover:bg-[#D4D4D8] dark:bg-[#3F3F46] dark:hover:bg-[#4F4F56]", readOnly && "cursor-not-allowed opacity-50")}
                                onClick={readOnly ? null : () => handlePayout('withdrawal', data)}>
                                <ArrowOutwardOutlinedIcon className="text-[#18181B] dark:text-[#FFF]" />
                            </div>
                        </Tooltip>
                        <Tooltip arrow placement="top" title={'THIRD-PARTY PAYMENT'}>
                            <div
                                className={clsx("flex w-11 p-[10px] justify-center items-center gap-1 rounded-lg bg-[#E4E4E7] cursor-pointer hover:bg-[#D4D4D8] dark:bg-[#3F3F46] dark:hover:bg-[#4F4F56]", readOnly && "cursor-not-allowed opacity-50")}
                                onClick={readOnly ? null : () => handlePayout('paymentOut', data)}>
                                <ListAltOutlinedIcon className="text-[#18181B] dark:text-[#FFF]" />
                            </div>
                        </Tooltip>
                    </div>
                </div>
            </div>
        </TransactionCommonModal>
    );
};

export default SavedAccountAddress;
